import React, { useState, useEffect } from "react";
import OTPInput from "../components/ui/OTPInput";
import { requestPasswordResetOTP, verifyOTP, resetPasswordWithOTP } from "../services/authService";
import logoFull from "../assets/logo-full.png";
import logoFullWhite from "../assets/logo-full-white.png";
import authBg from "../assets/auth-bg.png";

/** Matches app header accent (--accent) */
const BRAND_RED = "#a51c1c";

const ResetPasswordPage = () => {
  const navigate = (path) => {
    window.history.pushState({}, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [organizationCode, setOrganizationCode] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const emailParam = params.get("email");
      const orgParam = params.get("organizationCode");
      if (emailParam) setEmail(emailParam);
      if (orgParam) setOrganizationCode(orgParam);
    } catch {
      // ignore malformed query strings
    }
  }, []);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email) {
      setError("Email is required");
      return;
    }
    setLoading(true);
    try {
      const result = await requestPasswordResetOTP(email, organizationCode.trim());
      if (result.success) {
        setSuccess("Password reset code has been sent to your email");
        setStep(2);
        setResendCooldown(60);
      } else {
        setError(result.message || "Failed to send reset code");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Request OTP error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      const result = await requestPasswordResetOTP(email, organizationCode.trim());
      if (result.success) {
        setSuccess("New code has been sent to your email");
        setResendCooldown(60);
      } else {
        setError(result.message || "Failed to resend code");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Resend OTP error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (otpCode) => {
    setOtp(otpCode);
    setError("");
    setSuccess("");
    if (otpCode.length !== 6) return;
    setLoading(true);
    try {
      const result = await verifyOTP(email, otpCode);
      if (result.success) {
        setSuccess("OTP verified successfully");
        setStep(3);
      } else {
        setError(result.message || "Invalid OTP code");
        setOtp("");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Verify OTP error:", err);
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push("Password must be at least 8 characters long");
    if (!/[A-Z]/.test(password)) errors.push("Password must contain at least one uppercase letter");
    if (!/[a-z]/.test(password)) errors.push("Password must contain at least one lowercase letter");
    if (!/[0-9]/.test(password)) errors.push("Password must contain at least one number");
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) errors.push("Password must contain at least one special character");
    return errors;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!newPassword || !confirmPassword) {
      setError("All fields are required");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setError(passwordErrors[0]);
      return;
    }
    setLoading(true);
    try {
      const result = await resetPasswordWithOTP(email, otp, newPassword);
      if (result.success) {
        setSuccess("Password reset successfully! Redirecting to login...");
        setTimeout(() => navigate("/"), 2000);
      } else {
        setError(result.message || "Failed to reset password");
        if (result.details) setError(result.details.join(", "));
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Reset password error:", err);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "block w-full py-3 px-4 border text-sm focus:outline-none";
  const inputStyle = { borderColor: "#ddd", borderWidth: "1.6px" };

  return (
    <div
      className="min-h-screen flex justify-center items-center p-6"
      style={{ background: BRAND_RED }}
    >
      <div className="bg-white w-full max-w-[1000px] flex flex-col lg:flex-row shadow-lg">
        {/* Left column: dark panel */}
        <div
          className="hidden lg:flex lg:w-1/2 flex-col justify-center p-10 md:p-12 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${authBg})`,
            backgroundColor: "#1a1d21",
          }}
        >
          <img src={logoFullWhite} alt="JEBL Solutions" className="h-[60px] w-auto object-contain" />
        </div>

        {/* Right column: form */}
        <div className="w-full lg:w-1/2 flex flex-col">
          <div className="flex-1 px-8 pt-12 pb-8 md:px-10 md:pt-[80px] md:pb-8 md:min-h-[520px]">
            <div className="lg:hidden text-center mb-6">
              <img src={logoFull} alt="JEBL Solutions" className="h-12 mx-auto" />
            </div>

            <h4 className="text-[20px] font-semibold text-gray-900 mt-0 mb-1">
              Forgot Password?
            </h4>
            <h5 className="text-[17px] font-medium text-gray-700 m-0">
              No worries, we'll send you reset instructions.
            </h5>

            {/* Progress */}
            <div className="flex justify-center my-6">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                  style={{ background: step >= 1 ? BRAND_RED : "#e5e7eb", color: step >= 1 ? "#fff" : "#6b7280" }}
                >
                  1
                </div>
                <div className="w-12 h-1 rounded" style={{ background: step >= 2 ? BRAND_RED : "#e5e7eb" }} />
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                  style={{ background: step >= 2 ? BRAND_RED : "#e5e7eb", color: step >= 2 ? "#fff" : "#6b7280" }}
                >
                  2
                </div>
                <div className="w-12 h-1 rounded" style={{ background: step >= 3 ? BRAND_RED : "#e5e7eb" }} />
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                  style={{ background: step >= 3 ? BRAND_RED : "#e5e7eb", color: step >= 3 ? "#fff" : "#6b7280" }}
                >
                  3
                </div>
              </div>
            </div>

            {error && (
              <div
                className="mb-4 text-sm text-center p-3 rounded border"
                style={{ background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" }}
              >
                {error}
              </div>
            )}
            {success && (
              <div
                className="mb-4 text-sm text-center p-3 rounded border"
                style={{ background: "#f0fdf4", color: "#166534", borderColor: "#bbf7d0" }}
              >
                {success}
              </div>
            )}

            {/* Step 1: Email */}
            {step === 1 && (
              <form onSubmit={handleRequestOTP} className="mt-6">
                <div className="mb-4">
                  <label className="block text-gray-800 font-semibold mb-1">Organization Code</label>
                  <input
                    type="text"
                    value={organizationCode}
                    onChange={(e) => setOrganizationCode(e.target.value)}
                    placeholder="Enter your organization code"
                    className={inputClass}
                    style={inputStyle}
                    disabled={loading}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Provided by your administrator. Optional for Super Admin access.
                  </p>
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-1">E-mail Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your Email"
                    className={inputClass}
                    style={inputStyle}
                    required
                    disabled={loading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full block py-3 px-4 text-center border-0 cursor-pointer mt-5 text-base font-medium text-white disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ background: BRAND_RED }}
                >
                  {loading ? "Sending..." : "Reset Password"}
                </button>
              </form>
            )}

            {/* Step 2: OTP */}
            {step === 2 && (
              <div className="mt-6 space-y-4">
                <p className="text-sm text-gray-600 text-center">
                  Enter the 6-digit code sent to <strong>{email}</strong>
                </p>
                <OTPInput
                  length={6}
                  onComplete={handleVerifyOTP}
                  onChange={(otpString) => setOtp(otpString)}
                  error={error}
                  disabled={loading}
                />
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || loading}
                    className="text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none"
                    style={{ color: BRAND_RED }}
                  >
                    {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend code"}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full text-sm text-gray-600 hover:text-gray-800 disabled:opacity-70"
                  disabled={loading}
                >
                  ← Back to email
                </button>
              </div>
            )}

            {/* Step 3: New Password */}
            {step === 3 && (
              <form onSubmit={handleResetPassword} className="mt-6 space-y-4">
                <div>
                  <label className="block text-gray-800 font-semibold mb-1">New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className={`${inputClass} pr-11`}
                      style={inputStyle}
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    At least 8 characters with uppercase, lowercase, number, and special character
                  </p>
                </div>
                <div>
                  <label className="block text-gray-800 font-semibold mb-1">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className={`${inputClass} pr-11`}
                      style={inputStyle}
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full block py-3 px-4 text-center border-0 cursor-pointer mt-2 text-base font-medium text-white disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ background: BRAND_RED }}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="w-full text-sm text-gray-600 hover:text-gray-800 mt-2"
                  disabled={loading}
                >
                  ← Back to OTP
                </button>
              </form>
            )}

            <p className="text-center mt-6">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="font-medium text-sm underline focus:outline-none"
                style={{ color: BRAND_RED }}
              >
                Back to Login
              </button>
            </p>
          </div>

          <div className="text-center py-4 text-[#929292] text-sm">
            © 2026 All Rights Reserved
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
